/**
 * Constantes del dominio del Optimizador de Cortes (cutlist).
 *
 * Permiso de gating + datos de presentación del editor (paleta de piezas, presets de
 * tablero). Los colores y presets replican los del SSR (`static/cutlist/cutlist.js`)
 * para que la pantalla React produzca el mismo contenido. El antiguo selector de
 * "niveles de optimización" se retiró (feature #75): el esfuerzo es interno y
 * adaptativo (ver `optimizer.ts`).
 */
import type { CutConfig, Part, Stock } from "./types";

/**
 * Permiso que gobierna el Optimizador de Cortes (igual que el menú/SSR/API:
 * `cutlist_controller.py` y `domains/cutlist.py` exigen `use_cutlist`; `navConfig.ts`
 * lo usa para la visibilidad del enlace).
 */
export const CUTLIST_PERMISSION = "use_cutlist";

/** Paleta de colores por pieza (idéntica a la fuente `cutlist.js`). */
export const PART_COLORS = [
  "#4E9AF1",
  "#F4A636",
  "#E8524A",
  "#58C48A",
  "#A97FE0",
  "#F06292",
  "#26C6DA",
  "#FF8A65",
  "#9FA8DA",
  "#66BB6A",
  "#EF5350",
  "#42A5F5",
  "#FFCA28",
  "#26A69A",
  "#EC407A",
  "#7E57C2",
  "#29B6F6",
  "#FFA726",
  "#AB47BC",
  "#78909C",
] as const;

/** Un preset de tablero (medida predefinida o personalizada). */
export interface StockPreset {
  id: "std" | "small" | "custom";
  label: string;
  /** Ancho/largo en cm; `null` en el preset "personalizado". */
  w: number | null;
  h: number | null;
  desc: string;
}

/** Presets de tablero (réplica de `PRESETS` del SSR). */
export const PRESETS: StockPreset[] = [
  { id: "std", label: "122 × 244 cm", w: 122, h: 244, desc: "Estándar MDF / Melamina" },
  { id: "small", label: "100 × 200 cm", w: 100, h: 200, desc: "Formato mediano" },
  { id: "custom", label: "Medida personalizada", w: null, h: null, desc: "" },
];

/** Margen del visor SVG (en px), igual que `PAD` del SSR. */
export const SVG_PAD = 40;

/** Piezas iniciales del editor (mismas que el estado inicial del SSR). */
export const INITIAL_PARTS: Part[] = [
  {
    id: 1,
    label: "Panel A",
    width: 60,
    height: 40,
    quantity: 4,
    canRotate: true,
    material: "",
    color: PART_COLORS[0],
  },
  {
    id: 2,
    label: "Panel B",
    width: 30,
    height: 30,
    quantity: 6,
    canRotate: true,
    material: "",
    color: PART_COLORS[1],
  },
  {
    id: 3,
    label: "Panel C",
    width: 80,
    height: 20,
    quantity: 2,
    canRotate: true,
    material: "",
    color: PART_COLORS[2],
  },
];

/** Tablero inicial del editor (122 × 244, ilimitado), igual que el SSR. */
export const INITIAL_STOCK: Stock = {
  id: 1,
  label: "122 × 244 cm",
  width: 122,
  height: 244,
  quantity: -1,
  material: "",
};

/** Configuración inicial del editor (kerf 0.3 cm). El esfuerzo del algoritmo es
 * interno y adaptativo (feature #75): ya no hay nivel que configurar. */
export const INITIAL_CONFIG: CutConfig = {
  cutThickness: 0.3,
  considerMaterial: false,
};

/** Mensaje genérico ante un error inesperado (no-API). */
export const CUTLIST_GENERIC_ERROR = "Ocurrió un error inesperado.";
